import React, { useEffect, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import type { EMPLOYMENT_TYPE } from 'cpool-constants';
import {
    AGGREGATE_NAMES,
    CUSTOMER_DATA_TABS,
    EMPLOYMENT_TYPES,
    LEAD_DETAILS_FORMS,
    LOAN_REQUEST_COMMANDS,
} from 'cpool-constants';
import type { SchemaLoanRequestResolver_Get } from 'cpool-tables/backend-resolvers';
import { diff, objectKeys } from 'cpool-utils';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';

import Azubi from './occupation/Azubi.tsx';
import Student from './occupation/Student.tsx';
import { getEmployment } from '../General/utils/getEmployment.ts';
import { getEmploymentValues } from '../General/utils/getEmploymentValues.ts';
import { APICommunicator } from '~/api/APICommunicator.ts';
import { TAX_CLASSIFICATIONS_FRONTEND } from '~/constants.ts';
import JobTermSelector from '~/features/leadDetails/components/Overview/CustomerData/Employment/inputs/JobTermSelector.tsx';
import { TaxClass } from '~/features/leadDetails/components/Overview/CustomerData/Employment/inputs/TaxClass.tsx';
import Employee from '~/features/leadDetails/components/Overview/CustomerData/Employment/occupation/Employee.tsx';
import Freelancer from '~/features/leadDetails/components/Overview/CustomerData/Employment/occupation/Freelancer.tsx';
import Housewife from '~/features/leadDetails/components/Overview/CustomerData/Employment/occupation/Housewife.tsx';
import Independent from '~/features/leadDetails/components/Overview/CustomerData/Employment/occupation/Independent.tsx';
import Official from '~/features/leadDetails/components/Overview/CustomerData/Employment/occupation/Official.tsx';
import Pensioner from '~/features/leadDetails/components/Overview/CustomerData/Employment/occupation/Pensioner.tsx';
import Unemployed from '~/features/leadDetails/components/Overview/CustomerData/Employment/occupation/Unemployed.tsx';
import Worker from '~/features/leadDetails/components/Overview/CustomerData/Employment/occupation/Worker.tsx';
import { hide } from '~/features/leadDetails/components/Overview/CustomerData/General/utils/hide.ts';
import { FormCard } from '~/features/leadDetails/components/Overview/CustomerData/shared/FormCard';
import ApplicantInfoContextProvider from '~/features/leadDetails/context/ApplicantInfoContext.tsx';
import { useLeadDetailsFormContext } from '~/features/leadDetails/context/LeadDetailsFormContext.tsx';
import { useTabErrorNotification } from '~/features/leadDetails/hooks/useTabErrorNotification.ts';
import type { Schema_BeschaeftigungForm } from '~/features/leadDetails/schemas/employment.schema.ts';
import { BeschaeftigungForm } from '~/features/leadDetails/schemas/employment.schema.ts';
import { useOnMountAndChange } from '~/hooks/useOnMountAndChange.tsx';

type Props = {
    lead: SchemaLoanRequestResolver_Get;
    secondApplicant?: boolean;
};

const EmploymentForm: React.FC<Props> = ({ lead, secondApplicant }) => {
    const formId = !secondApplicant
        ? LEAD_DETAILS_FORMS.EMPLOYMENT_APPLICANT1
        : LEAD_DETAILS_FORMS.EMPLOYMENT_APPLICANT2;
    const { registerDirtyState, registerReset, registerSubmit } = useLeadDetailsFormContext();

    const { currentEmploymentType, employmentData: initData } = getEmployment(lead, secondApplicant);

    const [activeEmploymentType, setActiveEmploymentType] = useState<EMPLOYMENT_TYPE | ''>(currentEmploymentType);

    const applicantId = secondApplicant && lead.antragsteller2 ? lead.antragsteller2.id : lead.antragsteller1.id;

    const {
        control,
        formState,
        getFieldState,
        getValues,
        handleSubmit,
        register,
        reset,
        setValue,
        trigger,
        watch,
        ...rest
    } = useForm<{
        beschaeftigung: Schema_BeschaeftigungForm;
    }>({
        defaultValues: { beschaeftigung: initData },
        mode: 'onChange',
        resolver: zodResolver(z.object({ beschaeftigung: BeschaeftigungForm })),
        shouldUnregister: true,
    });

    const employmentType = watch('beschaeftigung.beschaeftigungsart') ?? '';

    useTabErrorNotification(CUSTOMER_DATA_TABS.EMPLOYMENT, formState.errors, formId);

    useEffect(() => {
        registerDirtyState(formId, {
            dirty: formState.isDirty,
            dirtyFields: formState.dirtyFields,
        });
    }, [registerDirtyState, formId, formState, formState.isDirty]);

    const resetForm: typeof reset = (values, options) => {
        reset(values, options);
        setActiveEmploymentType(getValues('beschaeftigung.beschaeftigungsart') ?? '');
    };

    registerReset(formId, resetForm);

    useEffect(() => {
        const { employmentData } = getEmployment(lead, secondApplicant);
        if (!employmentData) return;

        if (
            (employmentType === employmentData.beschaeftigungsart && activeEmploymentType === employmentType) ||
            employmentType === '' ||
            activeEmploymentType === employmentType
        )
            return;

        const employmentValues = getEmploymentValues(employmentData);
        if (!employmentValues) return;

        const mapValues = (values: Record<string, unknown>, path?: string) => {
            objectKeys(values).forEach((key) => {
                if (values[key] !== null && typeof values[key] === 'object' && !(values[key] instanceof Date))
                    mapValues(values[key] as Record<string, unknown>, path ? `${path}.${key}` : `${key}`);
                else {
                    const fieldPath = path
                        ? `beschaeftigung.${employmentType.toLowerCase()}.${path}.${key}`
                        : `beschaeftigung.${employmentType.toLowerCase()}.${key}`;
                    // NOTE: This is a workaround to avoid type errors
                    (setValue as (path: string, value: unknown) => void)(fieldPath, values[key]);
                }
            });
        };

        mapValues(employmentValues);
        setActiveEmploymentType(employmentType);
    }, [employmentType, setValue, lead, secondApplicant, reset, setActiveEmploymentType, activeEmploymentType]);

    useOnMountAndChange(() => {
        registerSubmit(
            (setSubmitStatus) =>
                handleSubmit(
                    async (data) => {
                        if (!data.beschaeftigung) {
                            setSubmitStatus({ changes: false, saved: false });
                            return;
                        }

                        const oldData =
                            secondApplicant && lead.antragsteller2
                                ? lead.antragsteller2.beschaeftigung
                                : lead.antragsteller1.beschaeftigung;

                        if (data.beschaeftigung.steuerklasse === TAX_CLASSIFICATIONS_FRONTEND.NONE) {
                            // eslint-disable-next-line no-param-reassign
                            data.beschaeftigung.steuerklasse = null;
                        }

                        if (
                            !diff(oldData, data.beschaeftigung) ||
                            (oldData === null &&
                                data.beschaeftigung.steuerklasse === null &&
                                data.beschaeftigung.beschaeftigungsart === null)
                        ) {
                            setSubmitStatus({ changes: false, saved: false });
                            return;
                        }

                        try {
                            const { position } = await APICommunicator.sendCommand({
                                aggregateId: lead.id,
                                aggregateName: AGGREGATE_NAMES.LOAN_REQUEST,
                                commandName: LOAN_REQUEST_COMMANDS.changeApplicantEmployment,
                                payload: {
                                    applicantId,
                                    ...data.beschaeftigung,
                                },
                            });

                            setSubmitStatus({ changes: true, position: { forms: position }, saved: true });
                            // we need to provide the entire data object to reset the form otherwise we get problems with the dirty fields
                            resetForm(data, { keepValues: true });
                        } catch (error) {
                            setSubmitStatus({ changes: true, error, saved: false });
                        }
                    },
                    (error) => {
                        setSubmitStatus({ changes: true, error, saved: false });
                    },
                ),
            formId,
            () => trigger(),
        );
    }, [lead, applicantId, secondApplicant, trigger]);

    const switchEmploymentForm = (type: string): React.JSX.Element => {
        switch (type) {
            case EMPLOYMENT_TYPES.ANGESTELLTER:
                return <Employee secondApplicant={secondApplicant} />;
            case EMPLOYMENT_TYPES.ARBEITER:
                return <Worker secondApplicant={secondApplicant} />;
            case EMPLOYMENT_TYPES.ARBEITSLOSER:
                return <Unemployed secondApplicant={secondApplicant} />;
            case EMPLOYMENT_TYPES.BEAMTER:
                return <Official secondApplicant={secondApplicant} />;
            case EMPLOYMENT_TYPES.HAUSFRAU:
                return <Housewife secondApplicant={secondApplicant} />;
            case EMPLOYMENT_TYPES.FREIBERUFLER:
                return <Freelancer secondApplicant={secondApplicant} />;
            case EMPLOYMENT_TYPES.RENTNER:
                return <Pensioner secondApplicant={secondApplicant} />;
            case EMPLOYMENT_TYPES.SELBSTSTAENDIGER:
                return <Independent secondApplicant={secondApplicant} />;
            case EMPLOYMENT_TYPES.AZUBI:
                return <Azubi secondApplicant={secondApplicant} />;
            case EMPLOYMENT_TYPES.STUDENT:
                return <Student secondApplicant={secondApplicant} />;
            default:
                return <></>;
        }
    };

    return (
        <ApplicantInfoContextProvider applicantId={applicantId}>
            <FormProvider
                {...{
                    ...rest,
                    control,
                    formState,
                    getFieldState,
                    getValues,
                    handleSubmit,
                    register,
                    reset: resetForm,
                    secondApplicant,
                    setValue,
                    trigger,
                    watch,
                }}
            >
                <form
                    className="mb-2 space-y-2"
                    name={formId}
                    id={formId}
                    style={hide(!!(secondApplicant && lead.coApplicantHidden))}
                >
                    <FormCard>
                        <TaxClass secondApplicant={secondApplicant} />
                        <JobTermSelector secondApplicant={secondApplicant} />
                    </FormCard>
                    {/* keyed so the occupation sub-form remounts on type change; the key must not
                        include the shared fields above, or their just-changed values would be
                        unregistered and reset to the defaults (shouldUnregister: true) */}
                    <React.Fragment key={`${formId}-${employmentType}`}>
                        {switchEmploymentForm(employmentType)}
                    </React.Fragment>
                </form>
            </FormProvider>
        </ApplicantInfoContextProvider>
    );
};

export default EmploymentForm;
